import React, { useState, useCallback } from "react";
import { Rate, Input, Button, Space, Typography, Card } from "antd";
import { useIntl } from "react-intl";
import { SmileOutlined, CheckOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Text, Title } = Typography;

interface ChatEvaluationProps {
  onSubmit: (rating: number, feedback?: string) => void;
  onSkip: () => void;
  loading?: boolean;
}

const ChatEvaluation: React.FC<ChatEvaluationProps> = ({
  onSubmit,
  onSkip,
  loading = false,
}) => {
  const intl = useIntl();
  const [rating, setRating] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (rating === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(rating, feedback.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  }, [rating, feedback, onSubmit]);

  const handleSkip = useCallback(() => {
    onSkip();
  }, [onSkip]);

  const getRatingDesc = (value: number) => {
    const descriptions = [
      "", // 0星
      intl.formatMessage(
        { id: "evaluation.rating.1" },
        { defaultMessage: "非常不满意" }
      ),
      intl.formatMessage(
        { id: "evaluation.rating.2" },
        { defaultMessage: "不满意" }
      ),
      intl.formatMessage(
        { id: "evaluation.rating.3" },
        { defaultMessage: "一般" }
      ),
      intl.formatMessage(
        { id: "evaluation.rating.4" },
        { defaultMessage: "满意" }
      ),
      intl.formatMessage(
        { id: "evaluation.rating.5" },
        { defaultMessage: "非常满意" }
      ),
    ];
    return descriptions[value] || "";
  };

  return (
    <Card
      style={{
        margin: "16px 0",
        borderRadius: "12px",
        border: "1px solid #e6f7ff",
        background: "linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%)",
      }}
      bodyStyle={{ padding: "20px" }}
    >
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <SmileOutlined
          style={{
            fontSize: "32px",
            color: "#52c41a",
            marginBottom: "8px",
            display: "block",
          }}
        />
        <Title level={4} style={{ margin: "0 0 8px 0", color: "#262626" }}>
          {intl.formatMessage(
            { id: "evaluation.title" },
            { defaultMessage: "为本次对话评分" }
          )}
        </Title>
        <Text type="secondary">
          {intl.formatMessage(
            { id: "evaluation.subtitle" },
            { defaultMessage: "您的反馈有助于我们改进服务质量" }
          )}
        </Text>
      </div>

      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        <Rate
          value={rating}
          onChange={setRating}
          style={{ fontSize: "24px" }}
          disabled={loading || isSubmitting}
        />
        {rating > 0 && (
          <div style={{ marginTop: "8px" }}>
            <Text style={{ color: "#1890ff", fontWeight: 500 }}>
              {getRatingDesc(rating)}
            </Text>
          </div>
        )}
      </div>

      <div style={{ marginBottom: "20px" }}>
        <TextArea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder={intl.formatMessage(
            {
              id: "evaluation.feedback.placeholder",
            },
            {
              defaultMessage: "请分享您的使用体验或建议（可选）",
            }
          )}
          rows={3}
          maxLength={500}
          disabled={loading || isSubmitting}
          style={{ resize: "none" }}
          showCount
        />
      </div>

      <Space style={{ width: "100%", justifyContent: "center" }} size="middle">
        <Button
          onClick={handleSkip}
          disabled={loading || isSubmitting}
          style={{ minWidth: "80px" }}
        >
          {intl.formatMessage(
            { id: "evaluation.skip" },
            { defaultMessage: "跳过" }
          )}
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={rating === 0 || loading || isSubmitting}
          loading={isSubmitting}
          icon={<CheckOutlined />}
          style={{ minWidth: "80px" }}
        >
          {intl.formatMessage(
            { id: "evaluation.submit" },
            { defaultMessage: "提交评价" }
          )}
        </Button>
      </Space>
    </Card>
  );
};

export default ChatEvaluation;
